import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useShell } from "../shell/useShell";
import { LevelChip } from "./LevelChip";
import { ShareDialog } from "./ShareDialog";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import { log } from "../../logger";
import type { OwnCatalogEntry } from "../../shared/api/requests";

/**
 * Per-catalog management (catalog-selector 02, verdict 4b): rename, set
 * default, share and delete for one own catalog. The phone opens it as a
 * bottom sheet, tablet/desktop as a dialog; the rows and actions are identical.
 * Shared catalogs never reach this sheet (their rows are read-only).
 */
export function CatalogManageSheet({
  catalog,
  onClose,
}: {
  catalog: OwnCatalogEntry;
  onClose: () => void;
}) {
  const t = useT();
  const shell = useShell();
  const renameEntry = useAppStore((s) => s.renameEntry);
  const setDefaultEntry = useAppStore((s) => s.setDefaultEntry);
  const deleteEntry = useAppStore((s) => s.deleteEntry);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState(catalog.name);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const openRename = () => {
    setRenameName(catalog.name);
    setRenameOpen(true);
  };

  const submitRename = async () => {
    const name = renameName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      await renameEntry(catalog.id, name);
      setRenameOpen(false);
      onClose();
    } catch (err) {
      log.warn({ err, catalogId: shortId(catalog.id) }, "[catalog] Rename catalog failed");
    } finally {
      setBusy(false);
    }
  };

  const makeDefault = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await setDefaultEntry(catalog.id);
      onClose();
    } catch (err) {
      log.warn({ err, catalogId: shortId(catalog.id) }, "[catalog] Set default catalog failed");
    } finally {
      setBusy(false);
    }
  };

  const openDelete = () => {
    setDeleteTyped("");
    setDeleteOpen(true);
  };

  const submitDelete = async () => {
    if (deleteTyped !== catalog.name || busy) return;
    setBusy(true);
    try {
      // The store resolves the fallback current catalog; the page stays put.
      await deleteEntry(catalog.id);
      setDeleteOpen(false);
      onClose();
    } catch (err) {
      log.warn({ err, catalogId: shortId(catalog.id) }, "[catalog] Delete catalog failed");
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <Box data-testid="manage-sheet" sx={{ width: "100%", maxWidth: 480, mx: "auto", p: 2 }}>
      {shell === "phone" && (
        <Box
          aria-hidden
          sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: "divider", mx: "auto", mb: 1.5 }}
        />
      )}
      <DialogTitle sx={{ p: 0, fontWeight: 650 }}>{t("manage")}</DialogTitle>
      <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 1 }}>
        {catalog.name}
      </Typography>
      <LevelChip level="owner" />
      <List disablePadding sx={{ mt: 1 }}>
        <ListItemButton onClick={openRename} data-testid="manage-rename" sx={{ minHeight: 48, borderRadius: 1 }}>
          <ListItemText primary={t("entryRename")} />
        </ListItemButton>
        <ListItemButton
          disabled={catalog.isDefault || busy}
          onClick={() => void makeDefault()}
          data-testid="manage-default"
          sx={{ minHeight: 48, borderRadius: 1 }}
        >
          <ListItemText primary={t("entrySetDefault")} />
        </ListItemButton>
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="manage-default-hint"
          sx={{ display: "block", px: 2, pb: 0.5 }}
        >
          {t("defaultCatalogHint")}
        </Typography>
        <ListItemButton
          onClick={() => setShareOpen(true)}
          data-testid="manage-share"
          sx={{ minHeight: 48, borderRadius: 1 }}
        >
          <ListItemText primary={t("entryShare")} />
        </ListItemButton>
        <Divider sx={{ my: 0.5 }} />
        <ListItemButton
          onClick={openDelete}
          data-testid="manage-delete"
          sx={{ minHeight: 48, borderRadius: 1, color: "error.main" }}
        >
          <ListItemText primary={t("entryDelete")} primaryTypographyProps={{ color: "error" }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <>
      {shell === "phone" ? (
        <Drawer
          anchor="bottom"
          open
          onClose={onClose}
          slotProps={{
            paper: {
              // Same named-dialog contract as the tail sheet (spec §10).
              role: "dialog",
              "aria-modal": true,
              "aria-label": t("manage"),
              sx: { borderTopLeftRadius: 2, borderTopRightRadius: 2 },
            },
          }}
        >
          {body}
        </Drawer>
      ) : (
        <Dialog
          open
          onClose={onClose}
          fullWidth
          maxWidth="xs"
          aria-label={t("manage")}
        >
          {body}
        </Dialog>
      )}

      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} fullWidth maxWidth="xs" data-testid="rename-dialog">
        <DialogTitle>{t("entryRename")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label={t("entryName")}
            value={renameName}
            onChange={(event) => setRenameName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void submitRename();
            }}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} disabled={busy}>
            {t("cancel")}
          </Button>
          <Button variant="contained" disabled={!renameName.trim() || busy} onClick={() => void submitRename()}>
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      {shareOpen && (
        <ShareDialog
          entryId={catalog.id}
          initialGrants={catalog.acl}
          onClose={() => setShareOpen(false)}
        />
      )}

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
        data-testid="delete-entry-dialog"
      >
        <DialogTitle>{t("deleteEntryConfirmTitle", { name: catalog.name })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("deleteEntryConfirmBody")}
          </Typography>
          <TextField
            autoFocus
            label={t("entryName")}
            value={deleteTyped}
            onChange={(event) => setDeleteTyped(event.target.value)}
            fullWidth
            inputProps={{ "data-testid": "delete-entry-input" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy}>
            {t("cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteTyped !== catalog.name || busy}
            data-testid="delete-entry-confirm"
            onClick={() => void submitDelete()}
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** UUIDs are shortened in logs (CODING_STANDARDS). */
function shortId(id: string): string {
  return id.slice(0, 7);
}
